/**
 * Everything in the world that is not the building.
 *
 * One catalogue. A banquet round, a wine bottle, a mop and a scorpion are the
 * same kind of thing to every system that touches them: an entry here with a
 * mass and some tags. Nothing downstream is allowed to ask "is this a round" —
 * it asks whether it is `carryable`, how heavy it is, and what it says it can
 * be placed onto.
 *
 * `mass` is kilograms and it is the only number that matters. It decides how
 * many you can hold, how much they slow you down, whether they need both hands,
 * how far you can throw one and how hard it lands. Two dozen tuning tables
 * collapsed into one column the day this file existed.
 *
 * `tags` are what a system filters on:
 *   carryable   can be picked up
 *   fragile     breaks on a hard enough impact, loudly, into debris
 *   setup       a banquet event can call for it, and storage stocks it
 *   wheeled     rolls, has momentum, can be ridden
 *   edible      a consumable; see `consumables.js` for what it does
 *   food        something an animal wants; see `data/wildlife.js` for which
 *   creature    alive, and moves on its own until caught
 *
 * `place` is how an object says what it is for, so that placement code never
 * has to know. `onto` names what it wants underneath it and `role` names the
 * slot it fills. A chair is not special: it is a thing whose data says it goes
 * onto a `base` in the `seat` role, twelve to a base.
 */

/** Colour sets an object kind can come in. Only linen uses one so far. */
export const VARIANTS = {
  linenColour: {
    ivory: { id: 'ivory', name: 'Ivory', colour: 0xd8d0bc },
    white: { id: 'white', name: 'White', colour: 0xeceae4 },
    black: { id: 'black', name: 'Black', colour: 0x2e3138 },
    burgundy: { id: 'burgundy', name: 'Burgundy', colour: 0x7a2f3a },
    hunter: { id: 'hunter', name: 'Hunter Green', colour: 0x2f5a44 },
  },
};

/** Kept as a name because the BEO spec calls for a linen colour by name. */
export const LINENS = VARIANTS.linenColour;

/**
 * Turns a row of numbers into a catalogue entry.
 *
 * A prop is a mass, a colour and a size. Writing forty of them as full object
 * literals is four hundred lines in which the interesting column — the mass —
 * is impossible to scan. This keeps them as a table you can read down.
 */
function props(rows, extra = {}) {
  const out = {};
  for (const [id, name, mass, colour, radius, height, shape, tags = []] of rows) {
    out[id] = {
      id,
      name,
      plural: `${name}s`,
      mass,
      colour,
      radius,
      height,
      shape,
      // Heavier things land louder. One rule, no per-prop noise column.
      noise: Math.round(3 + Math.min(14, mass * 1.1)),
      ...extra,
      tags: ['carryable', 'prop', ...(extra.tags ?? []), ...tags],
    };
  }
  return out;
}

export const OBJECTS = {
  // --- banquet setup --------------------------------------------------------
  // These three were the entire object model until this file existed. They are
  // now three rows in it, and the masses are chosen so that the capacities and
  // burdens the loop was tuned against fall out of the general formula
  // unchanged: two rounds, twelve chairs, twelve linens.
  round: {
    id: 'round',
    name: '72" Round',
    plural: 'rounds',
    mass: 35,
    tags: ['carryable', 'setup'],
    colour: 0x9a7a52,
    /** Feet. A 72 inch round is six feet across. */
    radius: 3,
    height: 2.5,
    place: { onto: 'anchor', role: 'base' },
    noise: 16,
  },
  chair: {
    id: 'chair',
    name: 'Banquet Chair',
    plural: 'chairs',
    mass: 3.8,
    tags: ['carryable', 'setup'],
    colour: 0x5a7a9a,
    radius: 0.8,
    height: 1.5,
    place: { onto: 'base', role: 'seat', max: 12 },
    noise: 12,
  },
  linen: {
    id: 'linen',
    name: 'Linen',
    plural: 'linens',
    mass: 2,
    tags: ['carryable', 'setup'],
    colour: 0xb8b2a4,
    radius: 0.6,
    height: 0.6,
    variantSet: 'linenColour',
    place: { onto: 'base', role: 'cover' },
    noise: 4,
  },

  // --- staging --------------------------------------------------------------
  /*
   * The two things a sheet calls for that are not furniture.
   *
   * They are carryable and they are not `setup`: there is no anchor they go
   * onto and no role they fill, because a riser is not set on a table. What
   * makes a staging line met is the same rule that makes a linen missing — a
   * claimed thing that comes to rest outside the room it was claimed for is not
   * in the room, and one that comes to rest inside it is. Carry it in and put
   * it down. That is the whole mechanic and it is one already written.
   *
   * The masses are the design. A riser is a four by eight deck section and
   * weighs like one, so you get two and you walk slowly; a signage board is
   * light and awkward and the danger is entirely where it lives.
   */
  riser: {
    id: 'riser',
    name: 'Stage Riser',
    plural: 'risers',
    mass: 26,
    tags: ['carryable', 'staging'],
    colour: 0x7a6a4a,
    radius: 2,
    height: 0.7,
    noise: 18,
  },
  signage: {
    id: 'signage',
    name: 'Signage Board',
    plural: 'signage boards',
    mass: 7,
    tags: ['carryable', 'staging'],
    colour: 0x3c4658,
    radius: 1.4,
    height: 3,
    noise: 8,
  },

  // --- the first fragile things --------------------------------------------
  // Glass and bottles get no code anywhere. They are three rows here, and the
  // impact path in `throwing.js` does the rest: an energy threshold, a louder
  // noise, and debris that is itself an ordinary carryable object.
  glass: {
    id: 'glass',
    name: 'Water Glass',
    plural: 'glasses',
    mass: 0.3,
    tags: ['carryable', 'fragile'],
    colour: 0xa8bcc6,
    radius: 0.2,
    height: 0.5,
    shape: 'cylinder',
    noise: 5,
    fragile: { threshold: 1.2, noise: 30, debris: 'shard', pieces: 3 },
  },
  bottle: {
    id: 'bottle',
    name: 'Wine Bottle',
    plural: 'bottles',
    mass: 1.2,
    tags: ['carryable', 'fragile'],
    colour: 0x2f4632,
    radius: 0.25,
    height: 1.1,
    shape: 'cylinder',
    noise: 7,
    fragile: { threshold: 3, noise: 40, debris: 'shard', pieces: 4 },
  },
  shard: {
    id: 'shard',
    name: 'Broken Glass',
    plural: 'shards',
    mass: 0.08,
    tags: ['carryable', 'debris'],
    colour: 0xb6c6cc,
    radius: 0.15,
    height: 0.1,
    shape: 'box',
    noise: 3,
  },

  /*
   * --- the nuisance drawer --------------------------------------------------
   *
   * Five rows, and every difference between them is a number. A rubber band is
   * quiet and weak and goes a long way because it weighs two grams; a popper is
   * loud because its row says so; a handful of ice is a handful of ice. None of
   * them is a special case anywhere: they are thrown by `throwing.js`, carried
   * by `carry.js` and weighed by `mass.js` exactly like a wine bottle.
   */
  band: {
    id: 'band',
    name: 'Rubber Band',
    plural: 'rubber bands',
    mass: 0.002,
    tags: ['carryable', 'prank'],
    colour: 0xb8846a,
    radius: 0.06,
    height: 0.06,
    shape: 'cylinder',
    /*
     * Three feet, which is as quiet as anything in the catalogue. It is not a
     * hand-tuned exception: the general rule is 3 + mass * 1.1, and two grams
     * of rubber rounds to the floor of it.
     */
    noise: 3,
  },
  popper: {
    id: 'popper',
    name: 'Party Popper',
    plural: 'party poppers',
    mass: 0.05,
    tags: ['carryable', 'prank'],
    colour: 0xc8a15a,
    radius: 0.09,
    height: 0.22,
    shape: 'cylinder',
    /** The one prop in the game that is louder than breaking a bottle. */
    noise: 90,
  },
  ice: {
    id: 'ice',
    name: 'Handful of Ice',
    plural: 'handfuls of ice',
    mass: 0.35,
    tags: ['carryable', 'prank'],
    colour: 0xa8c4d2,
    radius: 0.14,
    height: 0.14,
    shape: 'box',
    noise: 4,
  },
  shrimp: {
    id: 'shrimp',
    name: "Yesterday's Shrimp",
    plural: 'shrimp',
    mass: 0.4,
    tags: ['carryable', 'prank'],
    colour: 0xc98a72,
    radius: 0.12,
    height: 0.1,
    shape: 'box',
    noise: 3,
  },
  salt: {
    id: 'salt',
    name: 'Salt',
    plural: 'salt',
    mass: 0.15,
    tags: ['carryable', 'prank'],
    colour: 0xe4e6ea,
    radius: 0.07,
    height: 0.14,
    shape: 'cylinder',
    noise: 3,
  },
  note: {
    id: 'note',
    name: 'Kick-Me Note',
    plural: 'notes',
    mass: 0.005,
    tags: ['carryable', 'prank'],
    colour: 0xeceae4,
    radius: 0.09,
    height: 0.01,
    shape: 'box',
    noise: 3,
  },
  napkin: {
    id: 'napkin',
    name: 'Peppered Napkin',
    plural: 'peppered napkins',
    mass: 0.02,
    tags: ['carryable', 'prank'],
    colour: 0xd9d2c4,
    radius: 0.1,
    height: 0.04,
    shape: 'box',
    noise: 3,
  },

  /*
   * What is on the tables at a lawn dinner, and so the reason a lawn dinner
   * and the desert do not mix. Content rather than a prop: a dinner sets it
   * out (`data/dinners.js`), so it lives up here with the things events use,
   * not among the forty that nothing may reference. Food to a javelina like
   * the orange in the break room, and by the same tag.
   */
  breadroll: {
    id: 'breadroll',
    name: 'Bread Roll',
    plural: 'bread rolls',
    mass: 0.08,
    tags: ['carryable', 'food'],
    colour: 0xc9a066,
    radius: 0.2,
    height: 0.15,
    shape: 'sphere',
    noise: 3,
  },

  // --- things with wheels ---------------------------------------------------
  // A cart is an object like any other: a mass, a size, and two extra numbers
  // saying how much it holds. Everything that makes it a cart — momentum,
  // rolling downhill, tipping when you corner it loaded, being ridden — is in
  // `wheeled.js` and applies to every row tagged this way. Adding a third kind
  // of cart is adding a row.
  tablecart: {
    id: 'tablecart',
    name: 'Table Cart',
    plural: 'table carts',
    mass: 60,
    tags: ['wheeled', 'rideable'],
    colour: 0x5d636d,
    radius: 1.6,
    height: 3,
    shape: 'box',
    noise: 14,
    /** Kilograms it will take, and the cap on however light the load is. */
    hold: { budget: 140, cap: 16 },
    /** How readily it rolls: metres per second squared lost to friction. */
    friction: 2.2,
    /** Cornering speed, loaded, above which it goes over. */
    tipAt: 2.6,
  },
  servicecart: {
    id: 'servicecart',
    name: 'Room Service Cart',
    plural: 'room service carts',
    mass: 34,
    tags: ['wheeled', 'rideable'],
    colour: 0x8f7d5e,
    radius: 1.3,
    height: 3,
    shape: 'box',
    noise: 11,
    hold: { budget: 70, cap: 14 },
    friction: 1.8,
    // Lighter, smaller wheels, goes over sooner. Nobody wrote that down; it is
    // one number and the same rule reads it.
    tipAt: 2,
  },
  /*
   * A chair truck: a tall narrow frame that banquet chairs are hung on in
   * stacks. Holds more of them than a table cart does because that is what it
   * is for, and goes over sooner because a stack of chairs is tall. Not
   * rideable; there is no deck on it to stand on. It came with the lawn
   * strikes and nothing about it is lawn-shaped, it is one more row.
   */
  chaircart: {
    id: 'chaircart',
    name: 'Chair Cart',
    plural: 'chair carts',
    mass: 30,
    tags: ['wheeled'],
    colour: 0x4a5a6d,
    radius: 1.4,
    height: 3,
    shape: 'box',
    noise: 12,
    hold: { budget: 100, cap: 24 },
    friction: 2,
    tipAt: 2.2,
  },

  /*
   * An ice cart: an insulated bin on four casters, the kind a steward runs ice
   * to an outlet in. Small wheels and a tall load of wet bags, so it goes over
   * sooner than a table cart does, which is one number like everything else.
   */
  icecart: {
    id: 'icecart',
    name: 'Ice Cart',
    plural: 'ice carts',
    mass: 45,
    tags: ['wheeled'],
    colour: 0xd8dde2,
    radius: 1.5,
    height: 3.2,
    shape: 'box',
    noise: 12,
    hold: { budget: 90, cap: 12 },
    friction: 2,
    tipAt: 2,
  },

  /*
   * The golf cart. Same class as the table cart, different numbers.
   *
   * Everything below is read by `wheeled.js` in place of the constant it would
   * otherwise use, and a cart row without them behaves exactly as it always
   * did. Feet, like the rest of this file.
   *
   *   climbs   metres of step it will take. A person takes a stair; a golf
   *            cart takes a kerb. The dock floor is four feet up a flight of
   *            stairs, so this one number is the whole of why it cannot get
   *            into the building — nothing anywhere says "outdoors only"
   *   drive    a motor instead of a foot: steady thrust, no kick, a wheel
   *            instead of castors so it does not wander
   *   seats    where people sit, as an offset from the middle (x across, z
   *            forward), the band a seated body fills, which way it faces,
   *            and what somebody in it adds to the load
   *   canopy   the roof, as a footprint and a height. A seat whose body is
   *            inside the footprint and under the roof is covered; nothing
   *            says which seat that is
   *   tows     a hitch ball, as feet behind the middle. A row with one can
   *            pull any other wheeled row; a row without one pulls nothing.
   *            What gets pulled is not named here or anywhere else
   */
  golfcart: {
    id: 'golfcart',
    name: 'Golf Cart',
    plural: 'golf carts',
    mass: 280,
    tags: ['wheeled', 'rideable'],
    colour: 0xd8d4c4,
    radius: 3.6,
    height: 6,
    shape: 'box',
    noise: 18,
    hold: { budget: 40, cap: 6 },
    friction: 1.1,
    // Alone, a full-lock turn at full speed holds. With somebody on the back
    // it goes over above about five metres a second.
    tipAt: 1.7,
    climbs: 0.18,
    drive: { thrust: 3.2, top: 6.5, turn: 1.4, kick: 0, wobble: 0, grip: 14 },
    seats: [
      { id: 'driver', at: [-0.9, 0.4], from: 2.4, to: 4.9, faces: 0, load: 0 },
      // The rear-facing bench on the back, where the bag well was. Behind the
      // roof, because that is where rear benches go.
      { id: 'rear', at: [0, -2.9], from: 2.4, to: 4.9, faces: Math.PI, load: 0.3 },
      // And beside the driver, under the roof: the seat the second of two
      // passengers gets. After the rear, so one passenger still rides on the back.
      { id: 'side', at: [0.9, 0.4], from: 2.4, to: 4.9, faces: 0, load: 0.3 },
    ],
    canopy: { rect: [-2.1, -1.4, 2.1, 2.3], at: 6 },
    // Just behind the rear bench, at the bumper.
    tows: { at: 3.9 },
  },

  // --- everything else ------------------------------------------------------
  // Forty-odd things the hotel simply has. None of them is referenced by a BEO,
  // a shortfall, a staff price or a task, and a test enforces that — the moment
  // a mission needs one of these, it stops being a prop and starts being
  // content, and the whole point of them is that they are neither.
  //
  // They exist so that the rules have something to be general about. A mass, a
  // colour and a couple of tags is the entire authoring cost of a thing you can
  // pick up, throw, break, hide behind or put somewhere it should not be.
  ...props([
    // id                 name                 mass   colour    r     h    shape      extras
    //
    // `food` is the animals' word, not the player's — `edible` is something
    // you can put in your own body on a shift. Nothing in the hotel is food to
    // the hotel; these four are food to a javelina, and the tag is how it
    // finds out.
    ['napkin',            'Napkin',            0.05, 0xe4e0d6, 0.4, 0.05, 'box'],
    ['sugar',             'Sugar Packet',      0.02, 0xf0ece2, 0.15, 0.05, 'box', ['food']],
    ['teaspoon',          'Teaspoon',          0.05, 0xb9c0c8, 0.25, 0.05, 'box'],
    ['matchbook',         'Matchbook',         0.03, 0x8e4b3c, 0.15, 0.08, 'box'],
    ['biro',              'Biro',              0.02, 0x2d3440, 0.2, 0.05, 'cylinder'],
    ['keycard',           'Key Card',          0.01, 0x394a63, 0.2, 0.02, 'box'],
    ['coaster',           'Coaster',           0.04, 0x6d5f4a, 0.3, 0.04, 'cylinder'],
    ['menu',              'Menu Card',         0.06, 0xd8cfb8, 0.5, 0.05, 'box'],
    ['remote',            'TV Remote',         0.15, 0x2a2f38, 0.2, 0.1, 'box'],
    ['soap',              'Bar of Soap',       0.1, 0xe8e2d0, 0.2, 0.1, 'box'],
    ['lemon',             'Lemon',             0.12, 0xd6bf4a, 0.25, 0.25, 'sphere', ['food']],
    ['apple',             'Apple',             0.2, 0x9c3f3a, 0.28, 0.28, 'sphere', ['food']],
    ['orange',            'Orange',            0.25, 0xd08a34, 0.3, 0.3, 'sphere', ['food']],
    ['tape',              'Roll of Tape',      0.2, 0x8a7550, 0.25, 0.2, 'cylinder'],
    ['saltshaker',        'Salt Shaker',       0.3, 0xd9d4c6, 0.16, 0.35, 'cylinder'],
    ['candle',            'Votive Candle',     0.3, 0xe0d5b2, 0.18, 0.3, 'cylinder'],
    ['coffeecup',         'Coffee Cup',        0.4, 0xd7d2c6, 0.25, 0.35, 'cylinder'],
    ['budvase',           'Bud Vase',          0.5, 0x7d94a0, 0.2, 0.7, 'cylinder'],
    ['sideplate',         'Side Plate',        0.5, 0xe2ded2, 0.45, 0.1, 'cylinder'],
    ['towel',             'Towel',             0.6, 0xdcd8cc, 0.5, 0.25, 'box'],
    ['clipboard',         'Clipboard',         0.7, 0x8a7550, 0.7, 0.08, 'box'],
    ['ashtray',           'Ashtray',           0.8, 0x5c6068, 0.3, 0.15, 'cylinder'],
    ['icebucket',         'Ice Bucket',        1.1, 0xa8adb6, 0.45, 0.6, 'cylinder'],
    ['coffeepot',         'Coffee Pot',        1.4, 0x3d4550, 0.4, 0.8, 'cylinder'],
    ['tray',              'Room Service Tray', 1.6, 0x8f7d5e, 1.4, 0.15, 'box'],
    ['radio',             'Radio',             1.8, 0x39424e, 0.6, 0.5, 'box'],
    ['broom',             'Broom',             2.2, 0x8a7550, 0.5, 3.6, 'box'],
    ['mop',               'Mop',               2.5, 0x7d8a6a, 0.5, 3.8, 'box'],
    ['firstaid',          'First Aid Kit',     3, 0xb5534b, 0.8, 0.5, 'box'],
    ['bucket',            'Mop Bucket',        3.2, 0x5a6b78, 0.7, 0.9, 'cylinder'],
    ['bustub',            'Bus Tub',           3.5, 0x4c5a66, 1.2, 0.6, 'box'],
    ['foldingsign',       'Wet Floor Sign',    4, 0xd0a32e, 0.7, 2.2, 'box'],
    ['wateringcan',       'Watering Can',      4, 0x5d7a55, 0.7, 0.9, 'cylinder'],
    ['chafer',            'Chafing Dish',      4.5, 0xa5aab2, 1.1, 0.7, 'box'],
    ['coffeeurn',         'Coffee Urn',        6, 0x8d939c, 0.6, 2.2, 'cylinder'],
    ['toolbox',           'Toolbox',           8, 0x8a5a3a, 0.9, 0.6, 'box'],
    // Ten-pound bags of ice, which is what an outlet short of ice is short of.
    ['icebag',            'Bag of Ice',        4.5, 0xdfeff5, 0.6, 0.5, 'box'],
    ['sandbag',           'Sandbag',           11, 0x9a8a68, 0.9, 0.5, 'box'],
    ['extinguisher',      'Fire Extinguisher', 14, 0xa83b32, 0.4, 2, 'cylinder'],
    ['plant',             'Potted Plant',      14, 0x3f6b42, 1, 3.2, 'cylinder'],
    ['cinderblock',       'Cinder Block',      16, 0x8d8d88, 0.8, 0.7, 'box'],
    ['luggage',           'Guest Luggage',     16, 0x4a3f3a, 1.2, 2, 'box'],
    ['icechest',          'Ice Chest',         18, 0x6f8494, 1.6, 1.2, 'box'],
    ['keg',               'Beer Keg',          28, 0x8d939c, 0.8, 2.4, 'cylinder'],
    ['buffer',            'Floor Buffer',      32, 0xb0842e, 1.1, 3.4, 'cylinder'],
  ]),

  // Fragile props. Same rows, plus the one extra field that makes them break.
  ...props(
    [
      ['wineglass',       'Wine Glass',        0.2, 0xbfd0d8, 0.2, 0.6, 'cylinder'],
      ['mirror',          'Hand Mirror',       0.4, 0xc8d4dc, 0.35, 0.1, 'cylinder'],
      ['carafe',          'Water Carafe',      0.9, 0xa9c2cc, 0.3, 0.9, 'cylinder'],
      ['platter',         'China Platter',     1.5, 0xe6e2d6, 0.9, 0.1, 'cylinder'],
      ['glassrack',       'Rack of Glassware', 9, 0x9fb4bc, 1.2, 1, 'box'],
    ],
    { tags: ['fragile'], fragile: { threshold: 1.6, noise: 34, debris: 'shard', pieces: 3 } }
  ),

  /*
   * The small Sonoran wildlife, as catalogue rows.
   *
   * They are here rather than in a bestiary because a caught scorpion is an
   * object. It has a mass, you can hold it, and you can throw it, through
   * exactly the same code that throws a wine bottle — which was the promise
   * step two made and this is it being kept. Nothing in throwing, carrying or
   * placement knows that any of these are alive.
   *
   * The big ones are not in here. You cannot pick up a javelina.
   */
  ...props(
    [
      ['scorpion',        'Scorpion',          0.03, 0x8a7a4a, 0.12, 0.08, 'box'],
      ['lizard',          'Whiptail Lizard',   0.06, 0x7a8a5a, 0.16, 0.1, 'box'],
      ['toad',            'Spadefoot Toad',    0.08, 0x6a7a52, 0.16, 0.14, 'box'],
      ['quail',           'Gambel\u2019s Quail', 0.18, 0x9a8a6a, 0.22, 0.28, 'cylinder'],
      ['bat',             'Bat',               0.02, 0x4a4248, 0.14, 0.1, 'box'],
      ['tarantula',       'Tarantula',         0.07, 0x5a4a3a, 0.18, 0.1, 'box'],
      ['roadrunner',      'Roadrunner',        0.3, 0x8a8272, 0.28, 0.5, 'cylinder'],
      ['rattlesnake',     'Rattlesnake',       1.2, 0x9a8a62, 0.3, 0.14, 'cylinder'],
    ],
    { tags: ['creature'] }
  ),
};

/**
 * Things that go in a pocket rather than a hand.
 *
 * Carried, they are not held out: there is no hold pose and nothing drawn in
 * the hand, and both arms swing with the walk. A rubber band held out one-handed
 * at the hip read as a man clutching his trousers. What goes on this list is
 * anything that fits a pocket: paper, a pen, a card, a sachet, a spoon, a
 * handful of ice in a napkin. Everything else of a size to hold is held.
 */
export const POCKETABLE = Object.freeze([
  'band', 'popper', 'ice', 'shrimp', 'salt', 'note', 'napkin', 'sugar', 'teaspoon',
  'matchbook', 'biro', 'keycard', 'coaster', 'menu', 'remote', 'soap',
]);
for (const id of POCKETABLE) OBJECTS[id].pocket = true;

/**
 * Feet from the centre of a round to a chair: the table's radius plus a working
 * gap. Lives here rather than in the renderer because both the geometry and the
 * seating maths need it, and a chair drawn somewhere other than where the game
 * thinks it is, is a chair you cannot reason about.
 */
export const SEAT_RADIUS_FEET = OBJECTS.round.radius + 1.5;

/** Everything with a tag. The only way any system should be selecting objects. */
export function objectsTagged(tag) {
  return Object.values(OBJECTS).filter((object) => object.tags.includes(tag));
}

export function hasTag(kind, tag) {
  return Boolean(OBJECTS[kind]?.tags.includes(tag));
}

export default OBJECTS;
